import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { PRODUCT_CATEGORIES, ROUND_FINISHES } from "@/data/products";
import { Reveal } from "@/components/Reveal";

const CATEGORY_INDUSTRY = {
  round: "Food Packaging",
  rect: "Food Packaging",
  bubble: "Food Packaging",
};

export const Products = ({ onEnquire }) => {
  const [active, setActive] = useState(PRODUCT_CATEGORIES[0].id);
  const cat = PRODUCT_CATEGORIES.find((c) => c.id === active);

  const enquire = (p) => {
    if (!onEnquire) return;
    onEnquire({
      key: Date.now(),
      industry: CATEGORY_INDUSTRY[active] || "",
      message: `Hi, I'm interested in pricing for the ${p.name} (${p.size}). Please share MOQ, lead time and rates.`,
    });
  };

  return (
    <section
      id="products"
      data-testid="products-section"
      className="relative bg-[#F5F1EA] py-24 md:py-40 border-t border-[#C9C3B4]"
    >
      <div className="max-w-[1400px] mx-auto px-5 sm:px-8">
        <Reveal className="max-w-3xl mb-10 md:mb-14">
          <span className="font-mono-tech text-[11px] uppercase tracking-[0.3em] text-[#0055FF]">
            / Product catalogue
          </span>
          <h2 className="font-display font-800 tracking-tighter text-3xl md:text-5xl lg:text-6xl mt-6 leading-[0.95]">
            What we mould today.
          </h2>
          <p className="text-[#55524B] mt-6 leading-relaxed">
            Our current production line — food-grade PP &amp; PE packaging,
            manufactured on all-electric injection moulding machines. Every item
            is BPA-free, microwave-safe and available in custom colours for OEM
            branding.
          </p>
        </Reveal>

        {/* Category tabs */}
        <div className="flex flex-wrap gap-2 md:gap-3 border-b border-[#C9C3B4] mb-12 md:mb-16" data-testid="product-tabs">
          {PRODUCT_CATEGORIES.map((c) => (
            <button
              key={c.id}
              data-testid={`product-tab-${c.id}`}
              onClick={() => setActive(c.id)}
              className="relative px-4 md:px-6 py-4 font-mono-tech text-[11px] md:text-xs uppercase tracking-widest transition-colors duration-300"
            >
              <span className={active === c.id ? "text-[#0F172A]" : "text-[#55524B] hover:text-[#0F172A]"}>
                {c.label}
              </span>
              {active === c.id && (
                <motion.span
                  layoutId="product-tab-underline"
                  className="absolute left-0 -bottom-px h-0.5 w-full bg-[#0055FF]"
                />
              )}
            </button>
          ))}
        </div>

        <AnimatePresence mode="wait">
          <motion.div
            key={active}
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
          >
            <p className="text-[#55524B] max-w-2xl mb-10 text-sm md:text-base">
              {cat.blurb}
            </p>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5">
              {cat.items.map((p) => (
                <div
                  key={p.size + p.name}
                  data-testid={`product-card-${active}-${p.size.replace(/\s+/g, "-").toLowerCase()}`}
                  className="group border border-[#C9C3B4] hover:border-[#0055FF]/60 transition-colors duration-400 flex flex-col"
                >
                  {/* studio plate */}
                  <div className="relative aspect-[4/3] overflow-hidden bg-gradient-to-b from-[#ECEAE4] to-[#DAD7CE]">
                    <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_30%,rgba(255,255,255,0.7),transparent_60%)]" />
                    <img
                      src={p.img}
                      alt={`${p.name} ${p.size}`}
                      loading="lazy"
                      className="relative z-10 h-full w-full object-contain p-5 group-hover:scale-105 transition-transform duration-700 ease-out"
                    />
                    <span className="absolute z-20 top-3 left-3 font-mono-tech text-[10px] uppercase tracking-widest bg-[#0055FF] text-white px-2.5 py-1">
                      {p.size}
                    </span>
                  </div>
                  {/* meta */}
                  <div className="p-5 md:p-6 flex-1 flex flex-col border-t border-[#C9C3B4]">
                    <h3 className="font-display font-700 text-lg tracking-tight text-[#0F172A]">
                      {p.name}
                    </h3>
                    <p className="text-[#55524B] text-sm mt-1.5 flex-1">{p.use}</p>
                    <p className="font-mono-tech text-[10px] uppercase tracking-widest text-[#0055FF] mt-4 pt-4 border-t border-[#C9C3B4]">
                      {p.dims}
                    </p>
                    <a
                      href="#quote"
                      onClick={() => enquire(p)}
                      data-testid={`product-enquire-${active}-${p.size.replace(/\s+/g, "-").toLowerCase()}`}
                      className="btn-industrial group/btn mt-4 inline-flex items-center justify-between gap-2 border border-[#C9C3B4] hover:border-[#0055FF] hover:bg-[#0055FF] text-[#55524B] hover:text-white px-4 py-3 font-mono-tech text-[10px] uppercase tracking-widest"
                    >
                      Get Pricing
                      <ArrowRight className="h-3.5 w-3.5 group-hover/btn:translate-x-1 transition-transform duration-300" />
                    </a>
                  </div>
                </div>
              ))}
            </div>

            {/* colours + finishes */}
            <div className="mt-10 flex flex-col md:flex-row md:items-center gap-6 md:gap-10 border border-[#C9C3B4] p-6 md:p-8">
              <div className="shrink-0">
                <span className="font-mono-tech text-[10px] uppercase tracking-widest text-[#0055FF]">
                  Available Colours
                </span>
                <div className="flex flex-wrap gap-2 mt-3">
                  {cat.colors.map((col) => (
                    <span
                      key={col}
                      className="font-mono-tech text-[10px] uppercase tracking-widest border border-[#C9C3B4] text-[#55524B] px-3 py-1.5"
                    >
                      {col}
                    </span>
                  ))}
                </div>
              </div>

              {active === "round" && (
                <div className="flex items-center gap-4 md:ml-auto">
                  {ROUND_FINISHES.map((f) => (
                    <div key={f.label} className="text-center">
                      <div className="h-16 w-16 md:h-20 md:w-20 bg-gradient-to-b from-[#ECEAE4] to-[#DAD7CE] overflow-hidden border border-[#C9C3B4]">
                        <img src={f.img} alt={f.label} className="h-full w-full object-contain p-1.5" />
                      </div>
                      <span className="font-mono-tech text-[9px] uppercase tracking-widest text-[#55524B] mt-2 block">
                        {f.label}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </motion.div>
        </AnimatePresence>

        <Reveal className="mt-12">
          <p className="text-[#55524B] text-sm md:text-base max-w-2xl">
            Looking for a part we don&apos;t list yet? We take on{" "}
            <span className="text-[#0F172A]">custom &amp; job moulding</span> across
            cosmetics, automotive and electronics.{" "}
            <a href="#quote" className="text-[#0055FF] link-underline pb-0.5">
              Request a quote →
            </a>
          </p>
        </Reveal>
      </div>
    </section>
  );
};
